// src/util/notification.ts
import Swal from "sweetalert2";

const DEFAULT_CONFIG = {
  confirmButtonColor: "#009ef7",
  cancelButtonColor: "#6c757d",
  customClass: {
    confirmButton: "btn btn-primary",
    cancelButton: "btn btn-light",
  },
  // Ensure SweetAlert2 appears above Bootstrap modals
  heightAuto: false,
  allowOutsideClick: true,
  allowEscapeKey: true,
};

export class NotificationService {
  /**
   * Show success notification
   */
  public static success(
    message: string,
    title: string = "Berhasil!"
  ): Promise<any> {
    return Swal.fire({
      title,
      text: message,
      icon: "success",
      timer: 3000,
      showConfirmButton: false,
      toast: true,
      position: "top-end",
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
    } as any);
  }

  /**
   * Show error notification
   */
  public static error(message: string, title: string = "Error!"): Promise<any> {
    return Swal.fire({
      title,
      text: message,
      icon: "error",
      confirmButtonText: "OK",
      ...DEFAULT_CONFIG,
    });
  }

  /**
   * Show warning notification
   */
  public static warning(
    message: string,
    title: string = "Peringatan!"
  ): Promise<any> {
    return Swal.fire({
      title,
      text: message,
      icon: "warning",
      confirmButtonText: "OK",
      ...DEFAULT_CONFIG,
    });
  }

  /**
   * Show info notification
   */
  public static info(
    message: string,
    title: string = "Informasi"
  ): Promise<any> {
    return Swal.fire({
      title,
      html: message, // Use html instead of text for formatted content
      icon: "info",
      confirmButtonText: "OK",
      width: "600px",
      ...DEFAULT_CONFIG,
    });
  }

  /**
   * Show confirmation dialog
   */
  public static confirm(
    message: string,
    title: string = "Konfirmasi",
    confirmText: string = "Ya",
    cancelText: string = "Batal"
  ): Promise<any> {
    return Swal.fire({
      title,
      text: message,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      reverseButtons: true,
      ...DEFAULT_CONFIG,
    });
  }

  /**
   * Show delete confirmation dialog
   */
  public static confirmDelete(
    message: string = "Data yang dihapus tidak dapat dikembalikan!",
    title: string = "Apakah Anda yakin?"
  ): Promise<any> {
    return Swal.fire({
      title,
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      reverseButtons: true,
      confirmButtonColor: "#f1416c",
      cancelButtonColor: "#6c757d",
      customClass: {
        confirmButton: "btn btn-danger",
        cancelButton: "btn btn-light",
      },
    });
  }

  /**
   * Prompt for password input
   */
  public static async promptPassword(
    title: string = "Masukkan Password",
    text: string = "Masukkan password perangkat",
    placeholder: string = "Password perangkat"
  ): Promise<string | null> {
    const { value: password } = await Swal.fire({
      title,
      text,
      input: "password",
      inputPlaceholder: placeholder,
      showCancelButton: true,
      confirmButtonText: "Konfirmasi",
      cancelButtonText: "Batal",
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value) {
          return "Password harus diisi!";
        }
        if (value.length < 3) {
          return "Password minimal 3 karakter!";
        }
        return null;
      },
      ...DEFAULT_CONFIG,
    });

    return typeof password === "string" ? password : null;
  }

  /**
   * Show loading dialog
   */
  public static loading(message: string = "Loading..."): void {
    Swal.fire({
      title: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    } as any);
  }

  /**
   * Close any open dialog
   */
  public static close(): void {
    Swal.close();
  }

  /**
   * Show stream key in a formatted dialog
   */
  public static async showStreamKey(
    streamKey: string,
    isNew: boolean = false
  ): Promise<void> {
    const title = isNew ? "Stream Key Baru" : "Stream Key";
    const message = isNew
      ? "Stream key berhasil diperbarui!"
      : "Stream key perangkat:";

    const html = `
      <div class="text-center">
        <p class="mb-4">${message}</p>
        <div class="mt-3 p-4 bg-light rounded">
          <div class="mb-3">
            <strong>Stream Key:</strong>
          </div>
          <code class="d-block p-3 bg-white border rounded" style="word-break: break-all; font-size: 14px; font-family: 'Courier New', monospace;">
            ${streamKey}
          </code>
          <button class="btn btn-sm btn-primary mt-3" onclick="navigator.clipboard.writeText('${streamKey}').then(() => Swal.fire({title: 'Berhasil!', text: 'Stream key berhasil disalin', icon: 'success', timer: 1500, showConfirmButton: false}))">
            <i class="bi bi-clipboard"></i> Copy ke Clipboard
          </button>
        </div>
        <div class="mt-3">
          <small class="text-muted">
            <i class="bi bi-exclamation-triangle"></i>
            Simpan stream key ini dengan aman! Stream key digunakan untuk autentikasi perangkat IoT.
          </small>
        </div>
      </div>
    `;

    await Swal.fire({
      title,
      html,
      icon: "success",
      confirmButtonText: "OK",
      width: "550px",
      ...DEFAULT_CONFIG,
    });
  }

  /**
   * Show connection test result
   */
  public static async showConnectionResult(
    success: boolean,
    device?: any,
    error?: string
  ): Promise<void> {
    if (success && device) {
      const html = `
        <div class="text-center">
          <div class="mb-4">
            <i class="bi bi-wifi text-success" style="font-size: 3rem;"></i>
          </div>
          <p class="mb-4">Koneksi ke perangkat berhasil!</p>
          <div class="text-start bg-light p-3 rounded">
            <div class="row mb-2">
              <div class="col-4"><strong>ID:</strong></div>
              <div class="col-8">${device.id}</div>
            </div>
            <div class="row mb-2">
              <div class="col-4"><strong>Nama:</strong></div>
              <div class="col-8">${device.name || "Tidak ada"}</div>
            </div>
            <div class="row mb-2">
              <div class="col-4"><strong>Lokasi:</strong></div>
              <div class="col-8">${device.location || "Tidak ada"}</div>
            </div>
            <div class="row">
              <div class="col-4"><strong>Status:</strong></div>
              <div class="col-8">
                <span class="badge badge-light-success">Online</span>
              </div>
            </div>
          </div>
        </div>
      `;

      await Swal.fire({
        title: "Test Koneksi Berhasil",
        html,
        icon: "success",
        confirmButtonText: "OK",
        width: "500px",
        ...DEFAULT_CONFIG,
      });
    } else {
      await Swal.fire({
        title: "Test Koneksi Gagal",
        html: `
          <div class="text-center">
            <div class="mb-4">
              <i class="bi bi-wifi-off text-danger" style="font-size: 3rem;"></i>
            </div>
            <p class="text-danger">${
              error || "Tidak dapat terhubung ke perangkat"
            }</p>
            <div class="mt-3">
              <small class="text-muted">
                Pastikan perangkat dalam keadaan online dan password yang dimasukkan benar.
              </small>
            </div>
          </div>
        `,
        icon: "error",
        confirmButtonText: "OK",
        ...DEFAULT_CONFIG,
      });
    }
  }

  /**
   * Show registration success with device details
   */
  public static async showRegistrationSuccess(
    deviceId: number,
    streamKey: string
  ): Promise<void> {
    const html = `
      <div class="text-center">
        <div class="mb-4">
          <i class="bi bi-check-circle text-success" style="font-size: 3rem;"></i>
        </div>
        <p class="mb-4">Perangkat IoT berhasil didaftarkan!</p>
        <div class="bg-light p-4 rounded mb-4">
          <div class="row mb-3">
            <div class="col-4"><strong>ID Perangkat:</strong></div>
            <div class="col-8">
              <code class="bg-white p-2 rounded">${deviceId}</code>
            </div>
          </div>
          <div class="mb-3">
            <strong>Stream Key:</strong>
          </div>
          <code class="d-block p-3 bg-white border rounded" style="word-break: break-all; font-size: 12px; font-family: 'Courier New', monospace;">
            ${streamKey}
          </code>
          <button class="btn btn-sm btn-primary mt-3" onclick="navigator.clipboard.writeText('${streamKey}').then(() => Swal.fire({title: 'Berhasil!', text: 'Stream key berhasil disalin', icon: 'success', timer: 1500, showConfirmButton: false}))">
            <i class="bi bi-clipboard"></i> Copy Stream Key
          </button>
        </div>
        <div class="alert alert-warning d-flex align-items-center" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <small>
            <strong>Penting:</strong> Simpan ID Perangkat dan Stream Key dengan aman. 
            Informasi ini diperlukan untuk konfigurasi perangkat IoT.
          </small>
        </div>
      </div>
    `;

    await Swal.fire({
      title: "Registrasi Berhasil",
      html,
      icon: "success",
      confirmButtonText: "OK",
      width: "600px",
      ...DEFAULT_CONFIG,
    });
  }

  /**
   * Show toast notification (non-blocking)
   */
  public static toast(
    message: string,
    type: "success" | "error" | "warning" | "info" = "info",
    duration: number = 3000
  ): Promise<any> {
    return Swal.fire({
      toast: true,
      position: "top-end",
      icon: type,
      title: message,
      showConfirmButton: false,
      timer: duration,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
      },
    } as any);
  }
}
